import { useState } from "react";
import { Button, Checkbox, Flex, Select, Typography } from "antd";
import { FilterOutlined } from "@ant-design/icons";
import { LateralModal } from "@/components/modals/LateralModal";
import type { AttributeSetRow } from "../types";

const { Text } = Typography;

export interface AttributeFiltersState {
  tipo?: string;
  conjunto?: number;
  /** Por nombre: cada conjunto tiene su propio "General" (B1.2, xcons-docs#408). */
  grupo?: string;
  soloFacetas: boolean;
}

const VACIO: AttributeFiltersState = {
  tipo: undefined,
  conjunto: undefined,
  grupo: undefined,
  soloFacetas: false,
};

interface AttributeFiltersFormProps {
  filters: AttributeFiltersState;
  applyFilters: (next: AttributeFiltersState) => void;
  tipos: string[];
  sets: AttributeSetRow[];
  grupos: Array<{ name: string; setCount: number }>;
  closeModal?: () => void;
}

const AttributeFiltersForm = ({
  filters,
  applyFilters,
  tipos,
  sets,
  grupos,
  closeModal,
}: AttributeFiltersFormProps) => {
  const [draft, setDraft] = useState<AttributeFiltersState>(filters);

  const aplicar = () => {
    applyFilters(draft);
    closeModal?.();
  };

  const limpiar = () => {
    setDraft(VACIO);
    applyFilters(VACIO);
    closeModal?.();
  };

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        flex: 1,
        overflow: "hidden",
      }}
    >
      <div style={{ flex: 1, overflowY: "auto", padding: "16px 24px" }}>
        <Flex vertical gap={16}>
          <div>
            <Text
              type="secondary"
              style={{ fontSize: 12, display: "block", marginBottom: 6 }}
            >
              Tipo
            </Text>
            <Select
              allowClear
              style={{ width: "100%" }}
              placeholder="Tipo"
              value={draft.tipo}
              onChange={(tipo) => setDraft((d) => ({ ...d, tipo }))}
              options={tipos.map((t) => ({ value: t, label: t }))}
            />
          </div>
          <div>
            <Text
              type="secondary"
              style={{ fontSize: 12, display: "block", marginBottom: 6 }}
            >
              Conjunto
            </Text>
            <Select
              allowClear
              style={{ width: "100%" }}
              placeholder="Conjunto"
              value={draft.conjunto}
              onChange={(conjunto) => setDraft((d) => ({ ...d, conjunto }))}
              options={sets.map((s) => ({
                value: s.attributeSetId,
                label: `${s.name} (${s.productCount.toLocaleString("es-AR")})`,
              }))}
            />
          </div>
          <div>
            <Text
              type="secondary"
              style={{ fontSize: 12, display: "block", marginBottom: 6 }}
            >
              Grupo
            </Text>
            <Select
              allowClear
              showSearch
              style={{ width: "100%" }}
              placeholder="Grupo"
              value={draft.grupo}
              onChange={(grupo) => setDraft((d) => ({ ...d, grupo }))}
              options={grupos.map((g) => ({
                value: g.name,
                label:
                  g.setCount > 1 ? `${g.name} (${g.setCount} conjuntos)` : g.name,
              }))}
            />
          </div>
          <Checkbox
            checked={draft.soloFacetas}
            onChange={(e) =>
              setDraft((d) => ({ ...d, soloFacetas: e.target.checked }))
            }
          >
            Sólo facetas
          </Checkbox>
        </Flex>
      </div>
      <Flex
        justify="end"
        gap={8}
        style={{
          flexShrink: 0,
          padding: "12px 24px",
          borderTop: "1px solid #f0f0f0",
          backgroundColor: "#fff",
        }}
      >
        <Button type="default" danger onClick={limpiar}>
          Borrar filtros
        </Button>
        <Button type="primary" icon={<FilterOutlined />} onClick={aplicar}>
          Filtrar
        </Button>
      </Flex>
    </div>
  );
};

interface AttributeFiltersButtonProps {
  filters: AttributeFiltersState;
  applyFilters: (next: AttributeFiltersState) => void;
  tipos: string[];
  sets: AttributeSetRow[];
  grupos: Array<{ name: string; setCount: number }>;
}

/** El mismo panel que el de Productos (`CatalogFiltersButton`), con los filtros de
 * Atributos (xcons-docs#390). */
export const AttributeFiltersButton = ({
  filters,
  applyFilters,
  tipos,
  sets,
  grupos,
}: AttributeFiltersButtonProps) => {
  const activos = [
    filters.tipo,
    filters.conjunto,
    filters.grupo,
    filters.soloFacetas,
  ].filter(
    Boolean,
  ).length;

  return (
    <LateralModal
      buttonName={activos ? `Filtros (${activos})` : "Filtros"}
      title="Filtros"
    >
      <AttributeFiltersForm
        filters={filters}
        applyFilters={applyFilters}
        tipos={tipos}
        sets={sets}
        grupos={grupos}
      />
    </LateralModal>
  );
};
